import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Laptop, 
  TrendingUp, 
  DollarSign, 
  Target, 
  Heart, 
  ShoppingBag, 
  Plane, 
  GraduationCap, 
  Clapperboard, 
  Leaf,
  Crown,
  Users,
  Eye,
  AlertCircle
} from 'lucide-react'
import { instagramService } from '@/services'
import type { SavedAnalyticsProfile } from '@/types'

const categories = [
  {
    id: 'mega-accounts',
    title: 'Mega Accounts',
    description: 'Ultra-high reach influencers with 100M+ followers',
    icon: Crown,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50/30 dark:bg-amber-950/10',
    borderColor: 'border-amber-200/20 dark:border-amber-800/20',
  },
  {
    id: 'technology',
    title: 'Technology Insights',
    description: 'Latest trends in AI, software development, and emerging tech',
    icon: Laptop,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50/30 dark:bg-blue-950/10',
    borderColor: 'border-blue-200/20 dark:border-blue-800/20',
  },
  {
    id: 'business',
    title: 'Business Development',
    description: 'Entrepreneurship, startups, and business strategy insights',
    icon: TrendingUp,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50/30 dark:bg-emerald-950/10',
    borderColor: 'border-emerald-200/20 dark:border-emerald-800/20',
  },
  {
    id: 'finance',
    title: 'Finance & Investment',
    description: 'Personal finance, investing, and market analysis',
    icon: DollarSign,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50/30 dark:bg-green-950/10',
    borderColor: 'border-green-200/20 dark:border-green-800/20',
  },
  {
    id: 'marketing',
    title: 'Marketing & Brand Strategy',
    description: 'Digital marketing, branding, and growth strategies',
    icon: Target,
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50/30 dark:bg-orange-950/10',
    borderColor: 'border-orange-200/20 dark:border-orange-800/20',
  },
  {
    id: 'health',
    title: 'Health & Wellness',
    description: 'Fitness, nutrition, mental health, and lifestyle wellness',
    icon: Heart,
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50/30 dark:bg-rose-950/10',
    borderColor: 'border-rose-200/20 dark:border-rose-800/20',
  },
  {
    id: 'ecommerce',
    title: 'E-commerce & Retail',
    description: 'Online selling, product reviews, and retail trends',
    icon: ShoppingBag,
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50/30 dark:bg-indigo-950/10',
    borderColor: 'border-indigo-200/20 dark:border-indigo-800/20',
  },
  {
    id: 'travel',
    title: 'Travel & Lifestyle',
    description: 'Travel guides, lifestyle content, and cultural insights',
    icon: Plane,
    color: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-50/30 dark:bg-cyan-950/10',
    borderColor: 'border-cyan-200/20 dark:border-cyan-800/20',
  },
  {
    id: 'education',
    title: 'Education & Learning',
    description: 'Educational content, tutorials, and skill development',
    icon: GraduationCap,
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50/30 dark:bg-violet-950/10',
    borderColor: 'border-violet-200/20 dark:border-violet-800/20',
  },
  {
    id: 'entertainment',
    title: 'Entertainment & Media',
    description: 'Comedy, music, movies, and entertainment content',
    icon: Clapperboard,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50/30 dark:bg-red-950/10',
    borderColor: 'border-red-200/20 dark:border-red-800/20',
  }
]

interface GlobalAccountCardProps {
  category: typeof categories[0]
  accounts: SavedAnalyticsProfile[]
  isLoading: boolean
}

function GlobalAccountCard({ category, accounts, isLoading }: GlobalAccountCardProps) {
  const navigate = useNavigate()
  const IconComponent = category.icon
  const categoryAccounts = accounts.filter(acc => 
    acc.target_username && 
    acc.response_code === 200 &&
    acc.category?.toLowerCase() === category.id.toLowerCase()  
  ).sort((a, b) => (b.profile_overview?.followers || 0) - (a.profile_overview?.followers || 0))
   .slice(0, 30) 

  return (
    <div>
      <Card className="h-full transition-all duration-200 hover:shadow-md hover:shadow-black/5 dark:hover:shadow-white/5 border border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex items-center justify-between">
            <div className={`p-2.5 rounded-lg ${category.bg} border border-border/20`}>
              <IconComponent className={`h-5 w-5 ${category.color}`} />
            </div>
            <Badge variant="secondary" className="text-xs font-medium bg-muted/50 text-muted-foreground">
              {categoryAccounts.length} accounts
            </Badge>
          </div>
          <div>
            <CardTitle className="text-lg font-semibold mb-2 text-foreground">{category.title}</CardTitle>
            <CardDescription className="text-sm leading-relaxed text-muted-foreground">
              {category.description}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4 h-[280px] flex flex-col pt-0">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-3 w-[150px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : categoryAccounts.length > 0 ? (
            <div className="space-y-2 flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
              {categoryAccounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border border-border/20">
                  <div className="flex flex-col space-y-1">
                    <div className="font-medium text-sm text-foreground">@{account.target_username}</div>
                    <div className="text-xs text-muted-foreground flex items-center space-x-3">
                      <span className="flex items-center">
                        <Users className="h-3 w-3 mr-1" />
                        {account.profile_overview?.followers?.toLocaleString() || 'N/A'}
                      </span>
                      <span className="flex items-center">
                        <Eye className="h-3 w-3 mr-1" />
                        {account.total_posts} posts
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => navigate(`/instagram/account/${account.id}`)}
                    className="text-xs hover:bg-background/80"
                  >
                    View Analytics
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No accounts available yet</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export function GlobalAccountsCategories() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['global-accounts'],
    queryFn: () => instagramService.getGlobalAccounts(),
    retry: 1,
  })

  if (error) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load global accounts. Please try again later.
        </AlertDescription>
      </Alert>
    )
  }

  const globalAccounts = data?.profiles || []

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((category) => (
          <GlobalAccountCard
            key={category.id}
            category={category}
            accounts={globalAccounts}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  )
} 